import React, { useState, useEffect } from 'react';
import * as CSS from './ageConsentBanner.module.scss';
import RichTextRenderer from 'components/richTextRenderer';
import Image from 'components/image'

// Constants for Session Storage Key (changed from localStorage)
const AGE_STORAGE_KEY = 'wkvoetbal_user_age';

// Helper to set the age range and manage the 'over-24' style
const setAgeAndStyle = (ageRange) => {
    if (typeof window !== 'undefined') {
        sessionStorage.setItem(AGE_STORAGE_KEY, ageRange);
        
        // --- Global Style Injection for Hiding Content ---
        const existingStyle = document.getElementById('age-gate-style');
        if (existingStyle) {
            existingStyle.remove();
        }

        if (ageRange !== '24_or_older') {
            const style = document.createElement('style');
            style.id = 'age-gate-style';
            style.innerHTML = '.over-24 { display: none !important; }';
            document.head.appendChild(style);
        }
    }
};

const AgeGate = ({ blok }) => {
    console.log(blok);
    
    // We start with null for SSR safety and let useEffect determine the truth.
    const [ageRange, setAgeRange] = useState(null); 
    // isPopupOpen starts as false (closed) for SSR safety.
    const [isPopupOpen, setIsPopupOpen] = useState(false);

    // 1. Core Logic: Runs only on the client after mount, with a slight delay.
    useEffect(() => {
        let timer;
        
        // 💡 DELAY ADDED HERE: Wrap the session storage check in a setTimeout.
        timer = setTimeout(() => {
            // Since this is inside useEffect, we are guaranteed to be on the client.
            const storedAge = sessionStorage.getItem(AGE_STORAGE_KEY);
            setAgeRange(storedAge);
            
            // Logic: Popup opens only if NO age is stored (storedAge is null)
            if (storedAge === null) {
                setIsPopupOpen(true);
            }
            
            // Apply the correct global style immediately
            setAgeAndStyle(storedAge || 'not_set'); 

        }, 50); // 50ms is usually enough to let hydration complete

        return () => {
            clearTimeout(timer); // Cleanup the timer on unmount or re-run
        }
    }, []); 

    // 2. Effect to manage body scroll lock based on popup state
    useEffect(() => {
        if (isPopupOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'unset';
        }

        return () => {
            document.body.style.overflow = 'unset';
        }
    }, [isPopupOpen]); 

    // Function to handle age selection and persistence
    const handleAgeSelection = (selectedAge) => {
        setAgeRange(selectedAge);
        setAgeAndStyle(selectedAge);
        setIsPopupOpen(false); // Close the modal
    };

    // Only render the modal when it's open
    return (
        <>
            {/* The Age Selection Popup/Modal - Only renders if isPopupOpen is true */}
            {isPopupOpen && (
                <div className={CSS.modalOverlay} role="dialog" aria-modal="true" aria-labelledby="modalTitle">
                    <div className={CSS.modalContent}>
                        <Image
                            img={blok.bannerImage}
                            sizes="600px"
                            loading="lazy"
                            className={CSS.modalLogo}
                        />
                        <div className={CSS.modalBody}>
                            {blok.label &&
                                <p id="modalTitle" className={CSS.modalTitle}>
                                    {blok.label}
                                </p>
                            }
                            <div className={CSS.modalText}>
                                <RichTextRenderer data={blok.text} />
                            </div>
                            
                            <div className={CSS.modalOptions}>
                                <button 
                                    onClick={() => handleAgeSelection('under_18')}
                                    className={CSS.optionButton}
                                    style={{
                                        backgroundColor: blok.declineBackgroundColor || '#dcdde1',
                                        color: blok.declineFontColor || '#163e6c'
                                    }}
                                >
                                    {blok.under18}
                                </button>
                                <button 
                                    onClick={() => handleAgeSelection('18_to_24')}
                                    className={CSS.optionButton}
                                    style={{
                                        backgroundColor: blok.declineBackgroundColor || '#dcdde1',
                                        color: blok.declineFontColor || '#163e6c'
                                    }}
                                >
                                    {blok.between18and24}
                                </button>
                                <button 
                                    onClick={() => handleAgeSelection('24_or_older')}
                                    className={CSS.optionButton + ' ' + CSS.confirmButton}
                                    style={{
                                        backgroundColor: blok.acceptBackgroundColor || '#163e6c',
                                        color: blok.acceptFontColor || '#ffffff'
                                    }}
                                >
                                    {blok.over24}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default AgeGate;